import { ChartBar, Columns, DotsThreeOutline, Rows, Tabs } from '@phosphor-icons/react';
import classNames from 'classnames';
import ValueSelectorModal from '../../../../../values/selector/ValueSelectorModal';
import { COMPONENT_GROUPS, COMPONENT_TYPE } from '../../../../constants';
import { componentRegistry } from '../../../../registry';
import { usePageEditor } from '../../../PageEditorProvider';
import SidebarItem from './SidebarItem';
import { useState, type ReactNode } from 'react';
import translations from '../../../../../../translations';
import type { ContainerType, PageElementType } from '../../../dnd-utils';

type ComponentMenuItem = {
    componentType?: 'Column' | 'Page' | undefined;
    type: string;
    label: string;
    icon: ReactNode;
    group: string | null;
    pageElementType: PageElementType;
    containerType?: ContainerType;
};

const SideBar = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const { componentRegistryList, selectComponentSuggestValue, container, state } =
        usePageEditor();

    const { preview } = state;

    const classes = classNames({
        'sidebar-left': true,
        preview,
    });

    const componentGroups = [
        COMPONENT_GROUPS['CONTAINERS'],
        COMPONENT_GROUPS['CONTENT'],
        COMPONENT_GROUPS['INPUT'],
        COMPONENT_GROUPS['DATA'],
    ];

    if (
        componentRegistryList.filter(
            (component) =>
                component.group === COMPONENT_GROUPS['CUSTOM'] &&
                component.componentType !== 'Column',
        ).length > 0
    ) {
        componentGroups.push(COMPONENT_GROUPS['CUSTOM']);
    }

    const renderComponentMenuItem = (groupedItems: ComponentMenuItem[], group: string) => {
        const menuItems = groupedItems.map((item) => {
            const props = {
                label: item.label,
                pageElementType: item.pageElementType,
                componentType: item.type.toLowerCase(),
                icon: item.icon,
                containerType: item.containerType,
            };

            return <SidebarItem key={item.type} {...props} />;
        });

        if (group !== COMPONENT_GROUPS['CUSTOM']) {
            // Find components that the custom set have overridden
            Object.entries(componentRegistry)
                .filter(([, standardComponent]) => standardComponent.group === group)
                .forEach(([standardKey, standardComponent]) => {
                    const registryItem = componentRegistryList.find(
                        (element) => element.type === standardKey,
                    );
                    // This component was a standard component
                    // but if it has now been overridden by a custom one
                    if (registryItem && registryItem.group === COMPONENT_GROUPS['CUSTOM']) {
                        // We push a disabled entry to explain why this standard entry can't be used in this Tenant
                        menuItems.push(
                            <SidebarItem
                                key={standardComponent.type}
                                label={standardComponent.ui.caption}
                                pageElementType="COMPONENT"
                                componentType={standardComponent.type.toLowerCase()}
                                icon={standardComponent.ui.icon}
                                overridden
                            />,
                        );
                    }
                });
        }

        return menuItems;
    };

    const containersToDisplay: ComponentMenuItem[] = [
        {
            pageElementType: 'CONTAINER',
            type: 'row',
            label: 'Row',
            icon: <Rows />,
            group: COMPONENT_GROUPS['CONTAINERS'],
            containerType: 'VERTICAL_FLOW',
        },
        {
            pageElementType: 'CONTAINER',
            type: 'column',
            label: 'Column',
            icon: <Columns />,
            group: COMPONENT_GROUPS['CONTAINERS'],
            containerType: 'HORIZONTAL_FLOW',
        },
        {
            pageElementType: 'CONTAINER',
            type: 'inline',
            label: 'Inline',
            icon: <DotsThreeOutline />,
            group: COMPONENT_GROUPS['CONTAINERS'],
            containerType: 'INLINE_FLOW',
        },
        {
            pageElementType: 'CONTAINER',
            type: 'tabs',
            label: 'Tabs',
            icon: <Tabs />,
            group: COMPONENT_GROUPS['CONTAINERS'],
            containerType: 'GROUP',
        },
    ];

    const groupsToDisplay = componentGroups.map((group) => {
        const items: ComponentMenuItem[] = componentRegistryList
            .filter((item) => item.group === group)
            .filter((item) => item.componentType !== 'Column')
            .filter((item) => {
                switch (item.type) {
                    case COMPONENT_TYPE['IMAGE']:
                    /** The image component is deprecated and should not show in the components list */
                    case COMPONENT_TYPE['FILES']:
                    /** The Files component is deprecated and should not show in the components list */
                    case COMPONENT_TYPE['HIDDEN']: {
                        /** The hidden component is deprecated and should not show in the components list */

                        return null;
                    }

                    default: {
                        return true;
                    }
                }
            })
            .map((component) => {
                return {
                    componentType: component.componentType,
                    type: component.type,
                    label: component.ui.caption,
                    icon: component.ui.icon,
                    group: component.group,
                    pageElementType: 'COMPONENT',
                };
            });

        if (group === COMPONENT_GROUPS['DATA']) {
            items.push({
                pageElementType: 'CONTAINER',
                type: 'chart',
                label: 'Chart',
                icon: <ChartBar />,
                group: COMPONENT_GROUPS['DATA'],
                containerType: 'CHARTS',
            });
        }

        if (group === COMPONENT_GROUPS['CONTAINERS']) {
            containersToDisplay.forEach((container) => {
                items.push(container);
            });
        }

        return {
            name: group,
            items: items.filter((item) =>
                item.label.toUpperCase().includes(searchQuery.toUpperCase()),
            ),
        };
    });

    return (
        <aside className={classes} data-testid="sidebar-left">
            <section className="margin-bottom">
                <input
                    className="form-control"
                    type="text"
                    placeholder={translations.COMMON_search}
                    onInput={(e) => {
                        setSearchQuery((e.currentTarget as HTMLInputElement).value);
                    }}
                />
            </section>
            <div className="sidebar-left-panels">
                <section className="flex-column">
                    <section className="component-group">
                        <div className="component-group-heading">Component Suggest</div>
                        <div className="padding">
                            <ValueSelectorModal
                                includeSystemValues={false}
                                value={null}
                                onChangeAsValueReference={(value) => {
                                    selectComponentSuggestValue(value);
                                }}
                                container={container}
                                forgetValueSelection={true}
                            />
                        </div>
                    </section>
                    {groupsToDisplay
                        .filter((group) => group.items.length > 0)
                        .map((group) => (
                            <section key={group.name} className="component-group">
                                <div className="component-group-heading">{group.name}</div>
                                {renderComponentMenuItem(group.items, group.name)}
                            </section>
                        ))}
                    {groupsToDisplay.every((group) => group.items.length === 0) ? (
                        <div className="no-results-message">No page components found</div>
                    ) : null}
                </section>
            </div>
        </aside>
    );
};

export default SideBar;
